// HopLayer — same-moment policy hops (Moment ADR-040 D4), drawn inside the
// column they belong to: event -> policy -> command, one beat of the story.
// Drawn above the tiles (inset from the right edge) because the stacked tiles
// leave no gap between them; the arrowhead lands on the reacting tile.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectConnections, selectFrames } from '../../store/slices/topologySlice.ts';
import { selectActualPath } from '../../store/slices/simulationSlice.ts';
import { deriveHops } from './hops.ts';
import type { NodePosition } from './layoutEngine.ts';
import type { RootState } from '../../store/index.ts';

const HOP_INK = '#6b3f7c'; // the Process swatch, darkened to read on the Event/Command/Process tiles

export default function HopLayer({
  nodePositions,
}: {
  nodePositions: Record<string, NodePosition>;
}) {
  const frames = useSelector(selectFrames);
  const connections = useSelector(selectConnections);
  const actualPath = useSelector(selectActualPath);
  const scenarioToTopo = useSelector((s: RootState) => s.topology.scenarioToTopologyNodeMap);
  const hops = useMemo(() => deriveHops(frames, connections), [frames, connections]);
  const walked = useMemo(
    () => new Set(actualPath.map((id) => scenarioToTopo[id] ?? id)),
    [actualPath, scenarioToTopo],
  );

  return (
    <g aria-hidden="true">
      {hops.map((hop) => {
        const a = nodePositions[hop.from];
        const b = nodePositions[hop.to];
        if (!a || !b) return null;
        const x = a.x + 6 + a.w - 12;
        const down = b.y > a.y;
        const y1 = down ? a.y + a.h - 7 : a.y + 7;
        const y2 = down ? b.y + 9 : b.y + b.h - 9;
        const dir = down ? 1 : -1;
        const active = walked.has(hop.to);
        return (
          <g key={hop.key} data-testid="hop" data-from={hop.from} data-to={hop.to}>
            <path
              d={`M${x},${y1} L${x},${y2}`}
              fill="none"
              stroke={HOP_INK}
              strokeWidth={active ? 2.25 : 1.5}
              opacity={active ? 1 : 0.75}
            />
            <path
              d={`M${x - 3.5},${y2 - dir * 5} L${x},${y2} L${x + 3.5},${y2 - dir * 5} Z`}
              fill={HOP_INK}
              opacity={active ? 1 : 0.75}
            />
          </g>
        );
      })}
    </g>
  );
}
